import { useState, useMemo } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// Priority order for overlapping cycles on the same day
const STATUS_PRIORITY = {
  IN_PROGRESS: 5,
  NEEDS_IRRIGATION: 4,
  FAILED_TOO_LONG: 3,
  FAILED_SHALLOW: 3,
  FAILED_UNEXPECTED_FLOOD: 3,
  ABORTED_FLOWERING: 2,
  SUCCEEDED: 1,
};

const DAY_COLORS = {
  SUCCEEDED: { bg: "bg-green-200", text: "text-green-900", hover: "hover:bg-green-300" },
  FAILED_SHALLOW: { bg: "bg-red-200", text: "text-red-900", hover: "hover:bg-red-300" },
  FAILED_TOO_LONG: { bg: "bg-red-300", text: "text-red-900", hover: "hover:bg-red-400" },
  FAILED_UNEXPECTED_FLOOD: { bg: "bg-orange-200", text: "text-orange-900", hover: "hover:bg-orange-300" },
  ABORTED_FLOWERING: { bg: "bg-purple-200", text: "text-purple-900", hover: "hover:bg-purple-300" },
  IN_PROGRESS: { bg: "bg-amber-200", text: "text-amber-900", hover: "hover:bg-amber-300" },
  FLOWERING_SAFE: { bg: "bg-blue-100", text: "text-blue-800", hover: "hover:bg-blue-200" },
  DEFAULT: { bg: "bg-gray-50", text: "text-gray-600", hover: "hover:bg-gray-100" },
};

function buildDayMap(cycles, cropCycle) {
  const map = {}; // "YYYY-MM-DD" → { status, cycle, isFlowering }

  // Mark flowering safe days
  if (cropCycle?.estimated_flowering_start && cropCycle?.estimated_flowering_end) {
    const safeStart = new Date(cropCycle.estimated_flowering_start);
    const safeEnd = new Date(cropCycle.estimated_flowering_end);
    safeStart.setDate(safeStart.getDate() - 7);
    safeEnd.setDate(safeEnd.getDate() + 7);
    let d = new Date(safeStart);
    while (d <= safeEnd) {
      const key = d.toISOString().slice(0, 10);
      map[key] = { status: "FLOWERING_SAFE", cycle: null, isFlowering: true };
      d.setDate(d.getDate() + 1);
    }
  }

  // Mark cycle days (may override flowering if higher priority)
  for (const cycle of cycles) {
    const start = new Date(cycle.drying_started_at);
    const end = cycle.drying_ended_at ? new Date(cycle.drying_ended_at) : new Date();
    let d = new Date(start);
    d.setHours(0, 0, 0, 0);
    const endDay = new Date(end);
    endDay.setHours(0, 0, 0, 0);

    while (d <= endDay) {
      const key = d.toISOString().slice(0, 10);
      const existing = map[key];
      const myPriority = STATUS_PRIORITY[cycle.status] || 0;
      const existingPriority = existing ? (STATUS_PRIORITY[existing.status] || 0) : 0;
      if (!existing || myPriority > existingPriority) {
        map[key] = { status: cycle.status, cycle, isFlowering: existing?.isFlowering || false };
      }
      d.setDate(d.getDate() + 1);
    }
  }

  return map;
}

export default function AWDCycleCalendar({ cycles = [], cropCycle = null, onCycleClick }) {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [tooltip, setTooltip] = useState(null); // { key, cycle, status }

  const dayMap = useMemo(() => buildDayMap(cycles, cropCycle), [cycles, cropCycle]);

  const firstDay = new Date(year, month, 1).getDay(); // 0=Sun
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const prevMonth = () => {
    if (month === 0) { setYear(y => y - 1); setMonth(11); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 11) { setYear(y => y + 1); setMonth(0); }
    else setMonth(m => m + 1);
  };

  const cells = [];
  // Leading empty cells
  for (let i = 0; i < firstDay; i++) cells.push(null);
  // Day cells
  for (let d = 1; d <= daysInMonth; d++) {
    const key = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    cells.push({ day: d, key, info: dayMap[key] || null });
  }

  const isToday = (key) => key === today.toISOString().slice(0, 10);

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <button onClick={prevMonth} className="p-1 rounded hover:bg-gray-100">
          <ChevronLeft className="w-5 h-5 text-gray-600" />
        </button>
        <span className="font-semibold text-gray-800">
          {MONTHS[month]} {year}
        </span>
        <button onClick={nextMonth} className="p-1 rounded hover:bg-gray-100">
          <ChevronRight className="w-5 h-5 text-gray-600" />
        </button>
      </div>

      {/* Day-of-week headers */}
      <div className="grid grid-cols-7 mb-1">
        {DAYS.map(d => (
          <div key={d} className="text-center text-xs font-medium text-gray-400 py-1">{d}</div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-0.5">
        {cells.map((cell, idx) => {
          if (!cell) return <div key={`empty-${idx}`} />;
          const { day, key, info } = cell;
          const colors = info ? (DAY_COLORS[info.status] || DAY_COLORS.DEFAULT) : DAY_COLORS.DEFAULT;
          const todayRing = isToday(key) ? "ring-2 ring-blue-500" : "";

          return (
            <div
              key={key}
              className={`relative aspect-square flex items-center justify-center text-sm rounded cursor-pointer select-none
                ${colors.bg} ${colors.text} ${colors.hover} ${todayRing}`}
              onClick={() => {
                if (info?.cycle) {
                  setTooltip(tooltip?.key === key ? null : { key, ...info });
                  if (onCycleClick && info.cycle) onCycleClick(info.cycle);
                }
              }}
              title={info ? info.status.replace(/_/g, " ") : undefined}
            >
              {day}
              {/* Dot indicator for days with cycle data */}
              {info && (
                <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-current opacity-60" />
              )}
            </div>
          );
        })}
      </div>

      {/* Tooltip / selected cycle info */}
      {tooltip?.cycle && (
        <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200 text-sm">
          <div className="flex items-center gap-2 mb-1">
            <span className={`font-semibold ${(DAY_COLORS[tooltip.status] || DAY_COLORS.DEFAULT).text}`}>
              {tooltip.status.replace(/_/g, " ")}
            </span>
          </div>
          <div className="text-gray-600">
            {new Date(tooltip.cycle.drying_started_at).toLocaleDateString()} →{" "}
            {tooltip.cycle.drying_ended_at
              ? new Date(tooltip.cycle.drying_ended_at).toLocaleDateString()
              : "ongoing"}
            {" "}({tooltip.cycle.duration_days.toFixed(1)} days)
          </div>
          {tooltip.cycle.min_water_level_cm != null && (
            <div className="text-gray-500 text-xs mt-1">
              Min depth: {tooltip.cycle.min_water_level_cm.toFixed(1)} cm
              {" · "}Target: {tooltip.cycle.dry_threshold_cm.toFixed(1)} cm
            </div>
          )}
          {tooltip.cycle.status_reason && (
            <div className="text-gray-500 text-xs mt-1 italic">{tooltip.cycle.status_reason}</div>
          )}
        </div>
      )}

      {/* Legend */}
      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
        {[
          ["SUCCEEDED", "Succeeded"],
          ["FAILED_SHALLOW", "Failed (shallow)"],
          ["FAILED_TOO_LONG", "Failed (overdue)"],
          ["IN_PROGRESS", "In progress"],
          ["FLOWERING_SAFE", "Flowering safe"],
          ["ABORTED_FLOWERING", "Aborted"],
        ].map(([status, label]) => (
          <span key={status} className="flex items-center gap-1">
            <span className={`inline-block w-3 h-3 rounded ${(DAY_COLORS[status] || DAY_COLORS.DEFAULT).bg}`} />
            {label}
          </span>
        ))}
      </div>
    </div>
  );
}
